import _ from 'underscore';
import React, { useCallback, useContext, useMemo } from 'react';
import { Message } from 'botframework-directlinejs';
import { ChatAdd24Regular } from '@fluentui/react-icons';
import { Button, mergeClasses } from '@fluentui/react-components';
import { I18nModel, I18nContext } from '@bingads-webui/copilot-i18n';

import { StaticMessageType } from '../../botTypes';
import { AIMessage, IAIMessage } from '../message';
import { useStaticMessageStyles } from './use-static-message-styles';
import { COMMON_ERR_MSG, DEFAULT_DISPLAY_CONFIG } from './static-message-config';

// Static messages types that will not be shown to users
const IgnoredStaticMessageTypes: StaticMessageType[] = [
  'StreamingCancelled',
  'EmptyReplyToIdToCancelStreaming',
  'NoDataAvailable',
  'StreamingCompleted',
];

export interface IStaticMessageProps {
  activity: Message;
  onClickStartOver?(staticMessage: StaticMessageType, activity: Message): void;
  onFeedbackButtonClick?: IAIMessage['onFeedbackButtonClick'];
  onReferenceClick?(url: string): void;
  isWelcomeMessageEnabled?: boolean;
}

export const StaticMessage: React.FC<IStaticMessageProps> = React.memo(({
  activity,
  onClickStartOver,
  onFeedbackButtonClick,
  onReferenceClick,
  isWelcomeMessageEnabled = true,
}) => {
  const styles = useStaticMessageStyles();
  const i18n = useContext(I18nContext) as I18nModel;
  const staticMessageType = (activity.value as any) as StaticMessageType;
  const config = DEFAULT_DISPLAY_CONFIG[staticMessageType];

  const activityWithText = useMemo<Message>(() => {
    const text = config ? _.map(config.msg, (m) => i18n.getString(m, { pointPlaceholder: '- ' })).join('\n') : i18n.getString(COMMON_ERR_MSG);

    // replace the fixed text of the staticmessage with helpful error message
    return {
      ...activity,
      text,
    };
  }, [activity, config, i18n]);

  const onClickHandler = useCallback(() => {
    onClickStartOver?.(staticMessageType, activity);
  }, [activity, onClickStartOver, staticMessageType]);

  const startOverBtn = config?.showStartOverBtn ? (
    <Button className={mergeClasses(styles.startOver, 'start-over-btn')} icon={<ChatAdd24Regular />} onClick={onClickHandler}>
      {i18n.getString(_TL_('Start over'))}
    </Button>
  ) : null;

  if (_.contains(IgnoredStaticMessageTypes, staticMessageType)) {
    return null;
  }

  if (!isWelcomeMessageEnabled && staticMessageType === 'Welcome') {
    return null;
  }

  return (
    <AIMessage
      className={mergeClasses('ai-static-message', staticMessageType, styles.root)}
      key={activityWithText.id}
      message={activityWithText}
      showReferences={config?.showReferences}
      showCopyButton={false}
      showFeedback={config?.showFeedback}
      action={startOverBtn}
      onFeedbackButtonClick={onFeedbackButtonClick}
      onReferenceClick={onReferenceClick}
    />
  );
});
